// Geboortevenster · Datamodel van het geboortevenster: segmenten (backend), lagen, eigenschapssleutels, payloads, intervallen, blokken en lanes.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { HdBodygraphPlanetaryActivation } from '../bodygraph/hd-bodygraph.models';
import { HdCrossAngleKey, HdEnergyTypeBase } from '../bodygraph/basic-properties/hd-chart-properties.types';
import { PhsCategory } from '../bodygraph/hd-phs-metadata';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';

/**
 * Charttypes met een eigen tijdstip in `birthData`, dus met een gewoon
 * geboortevenster: standaardcharts en charts van een moment (Just Now, Rave New
 * Year; Steven 2026-10-25). Een bewaard moment (Transit) draagt de natal chart
 * top-level en krijgt het momentvenster als overlay (`birth-window-moment.ts`);
 * cycle charts en composites krijgen niets (liggen vast door de natal chart).
 */
export const HD_BIRTH_WINDOW_CHART_TYPES: ReadonlySet<PlatformChartType> = new Set([
  PlatformChartType.Standard,
  PlatformChartType.JustNow,
]);

/**
 * Geboortevenster: modellen voor "wat zou er aan de chart veranderen als de
 * geboortetijd iets vroeger of later was" (spec
 * docs/superpowers/specs/2026-10-16-geboortevenster-analyse.md §2 en §4.2).
 *
 * Alle tijden zijn epoch-milliseconden in **geboortetijd-coördinaten**: ook de
 * segmenten van de designkant zijn al teruggerekend naar het geboortemoment
 * dat ze veroorzaakt (de backend doet dat), zodat één tijdas volstaat.
 */

export type HdBirthWindowSide = 'personality' | 'design';

/** Eén aaneengesloten stuk waarin een hemellichaam dezelfde poort/lijn (en voor Zon/Knopen kleur/toon) houdt. */
export interface HdBirthWindowSegment {
  readonly fromMs: number;
  readonly toMs: number;
  readonly gate: number;
  readonly line: number;
  /** Enkel gevuld voor Zon, Aarde, Noord- en Zuidknoop (opgelost op base-niveau). */
  readonly color?: number;
  readonly tone?: number;
  readonly base?: number;
}

export interface HdBirthWindowBodySegments {
  readonly side: HdBirthWindowSide;
  readonly planetId: number;
  readonly sourceKey: string;
  /** Aaneensluitend en geclipt op het venster; het eerste begint op `fromMs`, het laatste eindigt op `toMs`. */
  readonly segments: readonly HdBirthWindowSegment[];
}

export interface HdBirthWindowData {
  readonly birthMs: number;
  readonly fromMs: number;
  readonly toMs: number;
  readonly bodies: readonly HdBirthWindowBodySegments[];
  /** True wanneer de grenzen eerste-orde geschat zijn uit de snelheden (indicator), niet exact opgelost. */
  readonly estimated?: boolean;
}

/**
 * Lagen (feedbackronde 1, 2026-10-16): Basics (poorten én lijnen: type t/m
 * levenskruis), Planeten (poort.lijn per planeet en kant), Variabelen
 * (kleur/toon) en Base (gedempt, telt niet mee in de indicator).
 */
export type HdBirthWindowTier = 'basics' | 'planets' | 'variables' | 'base';

/** De lagen die de betrouwbaarheidsindicator kleurt (Base bewust niet). */
export const HD_BIRTH_WINDOW_INDICATOR_TIERS: readonly HdBirthWindowTier[] = ['basics', 'planets', 'variables'];
/** Alle lagen in paneelvolgorde. */
export const HD_BIRTH_WINDOW_TIERS: readonly HdBirthWindowTier[] = ['basics', 'planets', 'variables', 'base'];

export type HdBirthWindowPropertyKey =
  | 'type'
  | 'authority'
  | 'definition'
  | 'centers'
  | 'channels'
  | 'profile'
  | 'incarnationCross'
  | 'lifeCross'
  | 'variable'
  | 'determination'
  | 'cognition'
  | 'environment'
  | 'environmentResonance'
  | 'motivation'
  | 'sense'
  | 'perspective'
  | 'perspectiveResonance'
  | 'determinationBase'
  | 'environmentBase'
  | 'motivationBase'
  | 'perspectiveBase';

/** Volgorde van de lanes in het paneel (spec §3.2): belangrijkste eerst, per laag. */
export const HD_BIRTH_WINDOW_PROPERTY_ORDER: readonly HdBirthWindowPropertyKey[] = [
  'type', 'authority', 'definition', 'centers', 'channels',
  'profile', 'incarnationCross', 'lifeCross',
  'variable', 'determination', 'cognition', 'environment', 'environmentResonance',
  'motivation', 'sense', 'perspective', 'perspectiveResonance',
  'determinationBase', 'environmentBase', 'motivationBase', 'perspectiveBase',
];

export const HD_BIRTH_WINDOW_TIER_OF: Readonly<Record<HdBirthWindowPropertyKey, HdBirthWindowTier>> = {
  type: 'basics',
  authority: 'basics',
  definition: 'basics',
  centers: 'basics',
  channels: 'basics',
  profile: 'basics',
  incarnationCross: 'basics',
  lifeCross: 'basics',
  variable: 'variables',
  determination: 'variables',
  cognition: 'variables',
  environment: 'variables',
  environmentResonance: 'variables',
  motivation: 'variables',
  sense: 'variables',
  perspective: 'variables',
  perspectiveResonance: 'variables',
  determinationBase: 'base',
  environmentBase: 'base',
  motivationBase: 'base',
  perspectiveBase: 'base',
};

/**
 * Weergave-payload van een eigenschapswaarde. De builders blijven puur (geen
 * vertaling); de componenten zetten de payload om naar tekst met de bestaande
 * formatters (`humanDesign.properties.*`, `HdPhsInfoService`, kruisnamen).
 */
export type HdBirthWindowPayload =
  | { readonly kind: 'none' }
  | { readonly kind: 'type'; readonly base: HdEnergyTypeBase; readonly manifesting: boolean; readonly pure: boolean }
  | { readonly kind: 'i18n'; readonly key: string }
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'profile'; readonly personalityLine: number; readonly designLine: number }
  | { readonly kind: 'cross'; readonly gates: readonly [number, number, number, number]; readonly angleKey: HdCrossAngleKey; readonly nameKey?: string }
  | { readonly kind: 'list'; readonly items: readonly string[]; readonly itemKind: 'center' | 'channel' }
  | { readonly kind: 'phs'; readonly category: PhsCategory; readonly color: number; readonly tone: number; readonly part: 'color' | 'tone' }
  | { readonly kind: 'base'; readonly category: PhsCategory; readonly base: number };

export interface HdBirthWindowPropertyValue {
  /** Stabiele identiteit om waarden te vergelijken en blokken samen te smelten. */
  readonly id: string;
  readonly payload: HdBirthWindowPayload;
}

export type HdBirthWindowSnapshot = Readonly<Record<HdBirthWindowPropertyKey, HdBirthWindowPropertyValue>>;

/** Tijdsinterval waarin geen enkele activatie van cel verandert, met de activatieset van dat interval. */
export interface HdBirthWindowInterval {
  readonly fromMs: number;
  readonly toMs: number;
  readonly activations: readonly HdBodygraphPlanetaryActivation[];
}

/** Welke activatie op een grensmoment van cel wisselt (voor de blok-popup "wat verandert"). */
export interface HdBirthWindowChange {
  readonly side: HdBirthWindowSide;
  readonly planetId: number;
  readonly sourceKey: string;
  readonly from: HdBirthWindowSegment;
  readonly to: HdBirthWindowSegment;
}

export type HdBirthWindowBlockState = 'birth' | 'same' | 'different';

export interface HdBirthWindowListDiff {
  readonly added: readonly string[];
  readonly removed: readonly string[];
  readonly unchanged: readonly string[];
}

export interface HdBirthWindowBlock {
  readonly fromMs: number;
  readonly toMs: number;
  readonly value: HdBirthWindowPropertyValue;
  readonly state: HdBirthWindowBlockState;
  /** Enkel voor lijst-waarden (centers, kanalen): verschil t.o.v. de geboortewaarde. */
  readonly diff?: HdBirthWindowListDiff;
}

export interface HdBirthWindowLane {
  readonly key: HdBirthWindowPropertyKey;
  readonly tier: HdBirthWindowTier;
  readonly blocks: readonly HdBirthWindowBlock[];
}

export type HdBirthWindowEvaluate = (activations: readonly HdBodygraphPlanetaryActivation[]) => HdBirthWindowSnapshot;

/** Eén blok in een planetenlane: poort.lijn van één planeet op één kant. */
export interface HdBirthWindowPlanetBlock {
  readonly fromMs: number;
  readonly toMs: number;
  readonly gate: number;
  readonly line: number;
  readonly state: HdBirthWindowBlockState;
}

export interface HdBirthWindowPlanetTrack {
  readonly side: HdBirthWindowSide;
  readonly blocks: readonly HdBirthWindowPlanetBlock[];
}

/** Eén planeet met zijn personality- en designspoor. */
export interface HdBirthWindowPlanetLane {
  readonly planetId: number;
  readonly sourceKey: string;
  readonly tracks: readonly HdBirthWindowPlanetTrack[];
}

/** Eén poort-/lijnwissel van een planeet (voor de samenvattende markerlane). */
export interface HdBirthWindowPlanetChange {
  readonly ms: number;
  readonly side: HdBirthWindowSide;
  readonly planetId: number;
  readonly sourceKey: string;
  readonly from: HdBirthWindowSegment;
  readonly to: HdBirthWindowSegment;
}
